/**
 * LocalCommandsSection - the catalog of commands an agent may run on the
 * user's own machine, and this device's acceptances of them.
 *
 * Two audiences share the panel because they are two halves of one decision.
 * The top half is the project catalog: what a holder of `p.local.manage`
 * declares an agent may ask for. The bottom half only appears in the desktop
 * app and is the other half of the handshake — nothing runs here until the
 * owner of *this machine* accepts the entry, and any security-relevant edit
 * upstream moves its hash and costs a fresh acceptance.
 *
 * Follows the Repositories/Plane shape: per-project configuration edited from
 * the global settings screen, with the project picker at the top.
 */
import {
  type LocalCommandTemplate,
  type LocalExecAcceptedEntry,
  type ProjectId,
  type ProjectLocalAction,
  type Tag,
  type TagId,
} from "@taskin-code/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuthStore, useHasProjectPermission } from "../../auth/authStore";
import { newCommandId } from "../../lib/utils";
import {
  acceptLocalAction,
  hasLocalExecBridge,
  listAcceptedLocalActions,
  revokeLocalAction,
} from "../../localExecBridge";
import {
  LOCAL_ACTION_DEVICE_STATUS_LABEL,
  buildLocalActionFromDraft,
  draftFromLocalAction,
  draftFromLocalActionTemplate,
  emptyLocalActionArgDraft,
  emptyLocalActionDraft,
  localActionCommandText,
  orphanAcceptedEntries,
  resolveLocalActionDeviceStatus,
  type LocalActionArgDraft,
  type LocalActionDeviceStatus,
  type LocalActionDraft,
} from "../../localExecCatalog";
import { listLocalCommandTemplates } from "../../localExecTemplatesClient";
import { readNativeApi } from "../../nativeApi";
import { TagsClient } from "../../tags/TagsClient";
import { useStore } from "../../store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Field, ModalShell } from "./settingsModal";
import { TagPicker } from "./TagPicker";

const STATUS_CLASS: Record<LocalActionDeviceStatus, string> = {
  accepted: "text-emerald-400",
  changed: "text-amber-400",
  "not-accepted": "text-muted-foreground",
};

function EntrySummary(props: { entry: ProjectLocalAction }) {
  const { entry } = props;
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="truncate text-sm font-medium">{entry.title}</span>
        <span className="rounded bg-muted/50 px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
          {entry.kind === "pattern" ? "patrón" : "acción"}
        </span>
        <span className="rounded bg-muted/50 px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
          {entry.approval === "auto" ? "sin preguntar" : "pregunta siempre"}
        </span>
        {entry.mode === "detached" ? (
          <span className="rounded bg-muted/50 px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
            en segundo plano
          </span>
        ) : null}
        {/* The two badges that mean bytes move between the server and someone's
            laptop. Worth seeing without opening the entry. */}
        {entry.kind === "action" && entry.args.some((spec) => spec.type === "file") ? (
          <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">
            envía ficheros
          </span>
        ) : null}
        {entry.output !== "text" ? (
          <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">
            devuelve imagen
          </span>
        ) : null}
        {entry.isTemplate ? (
          <span
            className="rounded bg-brand/20 px-1.5 py-0.5 text-[10px] uppercase text-brand-foreground"
            title="Se ofrece como punto de partida al declarar comandos en otros proyectos."
          >
            plantilla
          </span>
        ) : null}
      </div>
      <code className="mt-1 block truncate text-xs text-muted-foreground">
        {localActionCommandText(entry)}
      </code>
      {entry.description ? (
        <p className="mt-1 text-xs text-muted-foreground">{entry.description}</p>
      ) : null}
    </div>
  );
}

function ArgRow(props: {
  arg: LocalActionArgDraft;
  onChange: (next: LocalActionArgDraft) => void;
  onRemove: () => void;
}) {
  const { arg, onChange } = props;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border/60 p-2">
      <div className="flex gap-2">
        <Input
          className="flex-1"
          placeholder="nombre"
          value={arg.name}
          onChange={(event) => onChange({ ...arg, name: event.target.value })}
        />
        <Select
          value={arg.type}
          onValueChange={(value) => {
            if (!value) return;
            onChange({ ...arg, type: value as LocalActionArgDraft["type"] });
          }}
        >
          <SelectTrigger className="w-36" aria-label="Tipo del argumento">
            <SelectValue>{arg.type}</SelectValue>
          </SelectTrigger>
          <SelectPopup align="start" alignItemWithTrigger={false}>
            <SelectItem hideIndicator value="string">
              string
            </SelectItem>
            <SelectItem hideIndicator value="enum">
              enum
            </SelectItem>
            <SelectItem hideIndicator value="number">
              number
            </SelectItem>
            <SelectItem hideIndicator value="boolean">
              boolean
            </SelectItem>
            <SelectItem hideIndicator value="file">
              file
            </SelectItem>
          </SelectPopup>
        </Select>
        <Button type="button" variant="ghost" size="sm" onClick={props.onRemove}>
          Quitar
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch
            checked={arg.required}
            onCheckedChange={(checked) => onChange({ ...arg, required: checked })}
          />
          Obligatorio
        </label>
        {arg.type === "enum" ? (
          <Input
            className="flex-1"
            placeholder="valores permitidos, separados por comas"
            value={arg.enumValues}
            onChange={(event) => onChange({ ...arg, enumValues: event.target.value })}
          />
        ) : null}
        {arg.type === "string" ? (
          <Input
            className="flex-1"
            placeholder="expresión regular opcional"
            value={arg.pattern}
            onChange={(event) => onChange({ ...arg, pattern: event.target.value })}
          />
        ) : null}
        {arg.type === "file" ? (
          <>
            <Input
              className="flex-1"
              placeholder="extensiones permitidas: .apk, .aab"
              value={arg.fileExtensions}
              onChange={(event) => onChange({ ...arg, fileExtensions: event.target.value })}
            />
            <Input
              className="w-32"
              inputMode="numeric"
              placeholder="máx. MiB"
              value={arg.maxBytesMib}
              onChange={(event) => onChange({ ...arg, maxBytesMib: event.target.value })}
            />
          </>
        ) : null}
      </div>
      {arg.type === "file" ? (
        // The author is deciding what someone else's machine will download. Say
        // plainly what this argument does, because the value is picked by the
        // agent and only these two fields bound it.
        <p className="text-xs text-muted-foreground">
          El agente elige <strong>qué fichero del worktree del hilo</strong> se envía a la máquina
          del usuario; nunca elige dónde se guarda ni cómo se llama allí. Limita las extensiones y
          el tamaño para que sólo pueda mandar el artefacto que este comando espera.
        </p>
      ) : null}
    </div>
  );
}

/** Ids are only unique inside a project, so the select keys on both. */
const templateKey = (template: LocalCommandTemplate): string =>
  `${template.projectId}::${template.entry.id}`;

/** What each output mode means, in the words the author needs, not ours. */
const LOCAL_ACTION_OUTPUT_LABEL: Record<LocalActionDraft["output"], string> = {
  text: "Sólo texto (stdout y stderr)",
  "binary-stdout": "Una imagen por stdout (adb exec-out screencap -p)",
  outfile: "Una imagen en el fichero que le indiquemos ({outfile})",
};

function EntryDialog(props: {
  initial: LocalActionDraft;
  projectId: ProjectId;
  existingIds: ReadonlyArray<string>;
  /** Published commands from every project, best match first. Empty is normal. */
  templates: ReadonlyArray<LocalCommandTemplate>;
  /** The vocabulary, loaded once by the panel. Empty when nobody defined any. */
  availableTags: ReadonlyArray<Tag>;
  onClose: () => void;
  onSave: (entry: ProjectLocalAction) => Promise<void>;
}) {
  const [draft, setDraft] = useState<LocalActionDraft>(props.initial);
  const [tagIds, setTagIds] = useState<ReadonlyArray<TagId>>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const editingId = props.initial.id;

  // Only an entry that already exists has tags to read. A new one collects
  // them in the form and gets them written after its first save, when it
  // finally has an id.
  useEffect(() => {
    if (editingId === "") return;
    let cancelled = false;
    void TagsClient.localCommandTags(props.projectId, editingId)
      .then((ids) => {
        if (!cancelled) setTagIds(ids);
      })
      .catch(() => {
        /* No tags is the same screen as tags that would not load. */
      });
    return () => {
      cancelled = true;
    };
  }, [props.projectId, editingId]);

  const patch = (next: Partial<LocalActionDraft>) =>
    setDraft((current) => ({ ...current, ...next }));

  const save = async () => {
    const built = buildLocalActionFromDraft(draft, props.existingIds);
    if (!built.ok) {
      setError(built.reason);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await props.onSave(built.entry);
      // After the command, and on its own rail: `tag_assignments` is not
      // event-sourced. A failure here would leave a saved command with the
      // wrong tags, which is worth a message rather than a silent catch.
      //
      // Skipped entirely when the picker was not shown — someone without
      // `t.view` sees no vocabulary and holds no opinion, and writing the empty
      // set they never chose would strip the tags somebody else did choose.
      if (props.availableTags.length > 0) {
        await TagsClient.setLocalCommandTags(props.projectId, built.entry.id, tagIds);
      }
      props.onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar el comando.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      wide
      title={props.initial.id === "" ? "Nuevo comando local" : "Editar comando local"}
      onClose={props.onClose}
    >
      <div className="flex flex-col gap-3">
        {/* Only when adding. Offering it while editing would read as "replace
            this command with that one", which is not what copying a shape is. */}
        {props.initial.id === "" && props.templates.length > 0 ? (
          <Field label="Empezar desde una plantilla">
            <Select
              value=""
              onValueChange={(value) => {
                const template = props.templates.find(
                  (candidate) => templateKey(candidate) === value,
                );
                if (!template) return;
                setError(null);
                setDraft(draftFromLocalActionTemplate(template.entry));
              }}
            >
              <SelectTrigger className="w-full" aria-label="Plantilla">
                <SelectValue>Elegir una plantilla…</SelectValue>
              </SelectTrigger>
              <SelectPopup align="start" alignItemWithTrigger={false}>
                {props.templates.map((template) => (
                  <SelectItem
                    hideIndicator
                    key={templateKey(template)}
                    value={templateKey(template)}
                  >
                    {template.entry.title}
                    <span className="ms-2 text-xs text-muted-foreground">
                      {template.projectTitle}
                      {template.matchesProject ? " · encaja aquí" : ""}
                    </span>
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              Copia todos los campos en el formulario y te deja cambiarlos. Revisa el directorio: es
              una ruta de la máquina de quien escribió la plantilla.
            </p>
          </Field>
        ) : null}
        <Field label="Nombre">
          <Input value={draft.title} onChange={(event) => patch({ title: event.target.value })} />
        </Field>
        <Field label="Descripción (la ve el agente)">
          <Textarea
            rows={2}
            value={draft.description}
            onChange={(event) => patch({ description: event.target.value })}
          />
        </Field>
        <Field label="Tipo">
          <Select
            value={draft.kind}
            onValueChange={(value) => {
              if (!value) return;
              patch({ kind: value as LocalActionDraft["kind"] });
            }}
          >
            <SelectTrigger className="w-full" aria-label="Tipo de entrada">
              <SelectValue>
                {draft.kind === "pattern" ? "Patrón de comando" : "Acción con argumentos"}
              </SelectValue>
            </SelectTrigger>
            <SelectPopup align="start" alignItemWithTrigger={false}>
              <SelectItem hideIndicator value="pattern">
                Patrón de comando
              </SelectItem>
              <SelectItem hideIndicator value="action">
                Acción con argumentos
              </SelectItem>
            </SelectPopup>
          </Select>
        </Field>
        <Field label={draft.kind === "pattern" ? "Patrón" : "Comando (argv)"}>
          <Input
            value={draft.command}
            placeholder={draft.kind === "pattern" ? "adb *" : "adb -s {serial} shell {cmd}"}
            onChange={(event) => patch({ command: event.target.value })}
          />
        </Field>
        <p className="text-xs text-muted-foreground">
          {draft.kind === "pattern"
            ? "El primer token es siempre el programa literal. '*' es exactamente un argumento y '**' el resto, sólo como último token."
            : "El primer token es el programa literal. Cada {nombre} tiene que estar declarado abajo; el número de argumentos lo fija el catálogo, nunca el agente."}
        </p>

        {draft.kind === "action" ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">Argumentos</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => patch({ args: [...draft.args, emptyLocalActionArgDraft()] })}
              >
                Añadir argumento
              </Button>
            </div>
            {draft.args.map((arg, index) => (
              <ArgRow
                key={arg.rowId}
                arg={arg}
                onChange={(next) =>
                  patch({ args: draft.args.map((item, at) => (at === index ? next : item)) })
                }
                onRemove={() => patch({ args: draft.args.filter((_, at) => at !== index) })}
              />
            ))}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Aprobación">
            <Select
              value={draft.approval}
              onValueChange={(value) => {
                if (!value) return;
                patch({ approval: value as LocalActionDraft["approval"] });
              }}
            >
              <SelectTrigger className="w-full" aria-label="Aprobación">
                <SelectValue>
                  {draft.approval === "auto" ? "Sin preguntar" : "Preguntar siempre"}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="start" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="always_ask">
                  Preguntar siempre
                </SelectItem>
                <SelectItem hideIndicator value="auto">
                  Sin preguntar
                </SelectItem>
              </SelectPopup>
            </Select>
          </Field>
          <Field label="Ejecución">
            <Select
              value={draft.mode}
              onValueChange={(value) => {
                if (!value) return;
                patch({ mode: value as LocalActionDraft["mode"] });
              }}
            >
              <SelectTrigger className="w-full" aria-label="Modo de ejecución">
                <SelectValue>
                  {draft.mode === "detached" ? "En segundo plano" : "Esperar el resultado"}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="start" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="sync">
                  Esperar el resultado
                </SelectItem>
                <SelectItem hideIndicator value="detached">
                  En segundo plano
                </SelectItem>
              </SelectPopup>
            </Select>
          </Field>
          <Field label="Tiempo máximo (segundos)">
            <Input
              value={draft.timeoutSeconds}
              onChange={(event) => patch({ timeoutSeconds: event.target.value })}
            />
          </Field>
          <Field label="Salida máxima (KiB)">
            <Input
              value={draft.maxOutputKib}
              onChange={(event) => patch({ maxOutputKib: event.target.value })}
            />
          </Field>
        </div>

        {draft.kind === "action" ? (
          <Field label="Qué devuelve">
            <Select
              value={draft.output}
              onValueChange={(value) => {
                if (!value) return;
                patch({ output: value as LocalActionDraft["output"] });
              }}
            >
              <SelectTrigger className="w-full" aria-label="Qué devuelve la acción">
                <SelectValue>{LOCAL_ACTION_OUTPUT_LABEL[draft.output]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="start" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="text">
                  {LOCAL_ACTION_OUTPUT_LABEL.text}
                </SelectItem>
                <SelectItem hideIndicator value="binary-stdout">
                  {LOCAL_ACTION_OUTPUT_LABEL["binary-stdout"]}
                </SelectItem>
                <SelectItem hideIndicator value="outfile">
                  {LOCAL_ACTION_OUTPUT_LABEL.outfile}
                </SelectItem>
              </SelectPopup>
            </Select>
            {draft.output !== "text" ? (
              // The author is authorising a file to leave the user's machine and
              // be shown to a model. That is the sentence they should read
              // before saving, not after someone accepts it.
              <p className="mt-2 text-xs text-muted-foreground">
                Al terminar, el equipo del usuario <strong>enviará al servidor la imagen</strong>{" "}
                que el comando produzca y el agente la verá. Sólo se admiten imágenes (PNG, JPEG,
                WebP o GIF) y el comando tiene que esperar el resultado.
                {draft.output === "outfile" ? (
                  <>
                    {" "}
                    Escribe <code>{"{outfile}"}</code> en el comando, exactamente una vez: ahí irá
                    la ruta del fichero que debe escribir.
                  </>
                ) : null}
              </p>
            ) : null}
          </Field>
        ) : null}
        <Field label="Directorio de trabajo (ruta absoluta en tu máquina, opcional)">
          <Input
            value={draft.cwd}
            placeholder="Por defecto, tu carpeta personal"
            onChange={(event) => patch({ cwd: event.target.value })}
          />
        </Field>

        {props.availableTags.length > 0 ? (
          <Field label="Etiquetas">
            <TagPicker available={props.availableTags} selected={tagIds} onChange={setTagIds} />
            <p className="mt-1 text-xs text-muted-foreground">
              Sólo deciden dónde se ofrece este comando si lo publicas como plantilla: aparece
              primero en los proyectos cuyas etiquetas encajan.
            </p>
          </Field>
        ) : null}

        {error ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            Cancelar
          </Button>
          <Button type="button" disabled={saving} onClick={() => void save()}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

/**
 * @param projectId  When given, the panel drops its own project selector and
 *   its heading and speaks about that project only — how it is rendered from
 *   Settings → Proyectos, where the surrounding screen already answers "which
 *   project". Omitted, it is the standalone screen it has always been.
 */
export function LocalCommandsSection(props: { readonly projectId?: ProjectId } = {}) {
  const projects = useStore((state) => state.projects);
  const fixedProjectId = props.projectId;
  const [selectedProjectId, setSelectedProjectId] = useState<ProjectId | null>(null);
  const projectId = fixedProjectId ?? selectedProjectId;
  const [accepted, setAccepted] = useState<ReadonlyArray<LocalExecAcceptedEntry>>([]);
  const [templates, setTemplates] = useState<ReadonlyArray<LocalCommandTemplate>>([]);
  const [availableTags, setAvailableTags] = useState<ReadonlyArray<Tag>>([]);
  const [editing, setEditing] = useState<LocalActionDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Resolved once per render rather than at module load: `window.desktopBridge`
  // is injected by the preload script, and an old desktop build has no
  // `localExec` on it at all.
  const isDesktop = hasLocalExecBridge();

  useEffect(() => {
    if (fixedProjectId !== undefined || projectId !== null) return;
    const first = projects[0];
    if (first !== undefined) setSelectedProjectId(first.id);
  }, [projects, projectId, fixedProjectId]);

  const refreshAccepted = useCallback(async () => {
    if (!isDesktop) return;
    try {
      setAccepted(await listAcceptedLocalActions());
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudieron leer las aceptaciones.");
    }
  }, [isDesktop]);

  useEffect(() => {
    void refreshAccepted();
  }, [refreshAccepted]);

  const project = projects.find((candidate) => candidate.id === projectId) ?? null;
  const canManage = useHasProjectPermission(projectId, "p.local.manage");
  // Publishing reaches outside this project, so it is a global grant on top of
  // the project one — see `p.local.template` in the permission catalogue.
  const canPublish =
    useAuthStore((state) => state.globalPermissions.has("p.local.template")) && canManage;
  const entries = useMemo(() => project?.localActions ?? [], [project]);
  const orphans = useMemo(() => orphanAcceptedEntries(accepted, entries), [accepted, entries]);

  // The vocabulary, for the picker inside the dialog. Swallowed on failure:
  // somebody with `p.local.manage` and no `t.view` still edits commands, they
  // simply have nothing to tag them with.
  useEffect(() => {
    if (!canManage) return;
    void TagsClient.list()
      .then((rows) => setAvailableTags(rows.map((row) => row.tag)))
      .catch(() => setAvailableTags([]));
  }, [canManage]);

  // Loaded per project because the server ranks them against *this* project's
  // tags. A failure is silent on purpose: the list is an aid, and "Añadir
  // comando" has to work without it.
  useEffect(() => {
    if (projectId === null || !canManage) {
      setTemplates([]);
      return;
    }
    let cancelled = false;
    void listLocalCommandTemplates(projectId)
      .then((rows) => {
        if (!cancelled) setTemplates(rows);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, canManage, entries]);

  const persist = async (nextActions: ReadonlyArray<ProjectLocalAction>) => {
    const api = readNativeApi();
    if (!api || !project) throw new Error("No hay conexión con el servidor.");
    await api.orchestration.dispatchCommand({
      type: "project.meta.update",
      commandId: newCommandId(),
      projectId: project.id,
      localActions: nextActions,
    });
  };

  const saveEntry = async (entry: ProjectLocalAction) => {
    const exists = entries.some((candidate) => candidate.id === entry.id);
    await persist(
      exists
        ? entries.map((candidate) => (candidate.id === entry.id ? entry : candidate))
        : [...entries, entry],
    );
  };

  const removeEntry = async (entry: ProjectLocalAction) => {
    if (
      !window.confirm(
        `¿Borrar "${entry.title}"? Los dispositivos que lo aceptaron dejarán de poder ejecutarlo.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await persist(entries.filter((candidate) => candidate.id !== entry.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo borrar el comando.");
    } finally {
      setBusy(false);
    }
  };

  const setTemplate = async (entry: ProjectLocalAction, isTemplate: boolean) => {
    const api = readNativeApi();
    if (!api || !project) {
      setError("No hay conexión con el servidor.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Its own command, not a field on the catalog update: publishing is
      // gated by a different grant, so it travels on a different rail.
      await api.orchestration.dispatchCommand({
        type: "project.localAction.template.set",
        commandId: newCommandId(),
        projectId: project.id,
        actionId: entry.id,
        isTemplate,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cambiar la plantilla.");
    } finally {
      setBusy(false);
    }
  };

  const accept = async (entry: ProjectLocalAction) => {
    setBusy(true);
    setError(null);
    try {
      const result = await acceptLocalAction(entry);
      setAccepted(result.entries);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo aceptar el comando.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (entryId: string) => {
    setBusy(true);
    setError(null);
    try {
      setAccepted(await revokeLocalAction(entryId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo revocar el comando.");
    } finally {
      setBusy(false);
    }
  };

  if (projects.length === 0) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        No hay proyectos todavía. Crea uno desde el sidebar y vuelve aquí para declarar los comandos
        que un agente puede ejecutar en tu máquina.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {fixedProjectId === undefined ? (
        <>
          <div className="min-w-0">
            <h3 className="text-base font-semibold">Comandos locales</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Un agente remoto sólo puede ejecutar en tu máquina lo que aparece en este catálogo y
              además esté aceptado en el dispositivo. Nada se ejecuta en una shell: el comando se
              lanza como argv, sin expansión ni operadores.
            </p>
          </div>

          <Select
            value={projectId ?? ""}
            onValueChange={(value) => {
              if (!value) return;
              setSelectedProjectId(value as ProjectId);
            }}
          >
            <SelectTrigger className="w-full sm:w-96" aria-label="Proyecto">
              <SelectValue>{project?.name ?? "Elige un proyecto"}</SelectValue>
            </SelectTrigger>
            <SelectPopup align="start" alignItemWithTrigger={false}>
              {projects.map((candidate) => (
                <SelectItem hideIndicator key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">
          Un agente remoto sólo puede ejecutar en tu máquina lo que aparece en este catálogo y
          además esté aceptado en el dispositivo. Nada se ejecuta en una shell: el comando se lanza
          como argv, sin expansión ni operadores.
        </p>
      )}

      {error ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      ) : null}

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">Catálogo del proyecto</h4>
          {canManage ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditing(emptyLocalActionDraft())}
            >
              Añadir comando
            </Button>
          ) : null}
        </div>

        {!canManage ? (
          <p className="text-xs text-muted-foreground">
            Necesitas el permiso <code>p.local.manage</code> en este proyecto para editar el
            catálogo. Puedes ver lo que ya está declarado.
          </p>
        ) : null}

        {entries.length === 0 ? (
          <div className="rounded-md border border-border/60 bg-card/40 p-4 text-sm text-muted-foreground">
            Este proyecto no declara ningún comando local.
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start justify-between gap-3 rounded-md border border-border/60 bg-card/40 p-3"
              >
                <EntrySummary entry={entry} />
                {canManage ? (
                  <div className="flex shrink-0 gap-2">
                    {canPublish ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => void setTemplate(entry, !entry.isTemplate)}
                      >
                        {entry.isTemplate ? "Quitar de plantillas" : "Marcar plantilla"}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(draftFromLocalAction(entry))}
                    >
                      Editar
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => void removeEntry(entry)}
                    >
                      Borrar
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {isDesktop ? (
        <section className="flex flex-col gap-3">
          <div>
            <h4 className="text-sm font-semibold">Comandos en este dispositivo</h4>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Aceptar es una decisión de esta máquina y se guarda sólo aquí. Si el comando cambia en
              el servidor deja de valer y hay que volver a aceptarlo.
            </p>
          </div>

          {entries.length === 0 ? (
            <div className="rounded-md border border-border/60 bg-card/40 p-4 text-sm text-muted-foreground">
              Nada que aceptar todavía.
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {entries.map((entry) => {
                const status = resolveLocalActionDeviceStatus(entry, accepted);
                return (
                  <li
                    key={entry.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-card/40 p-3"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{entry.title}</div>
                      <code className="block truncate text-xs text-muted-foreground">
                        {localActionCommandText(entry)}
                      </code>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className={`text-xs ${STATUS_CLASS[status]}`}>
                        {LOCAL_ACTION_DEVICE_STATUS_LABEL[status]}
                      </span>
                      {status === "accepted" ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          onClick={() => void revoke(entry.id)}
                        >
                          Revocar
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={busy}
                          onClick={() => void accept(entry)}
                        >
                          {status === "changed" ? "Volver a aceptar" : "Aceptar"}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {orphans.length > 0 ? (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-muted-foreground">
                Aceptaciones de comandos que ya no están en este catálogo. No hacen daño — nada las
                puede invocar — pero puedes limpiarlas.
              </p>
              <ul className="flex flex-col gap-2">
                {orphans.map((record) => (
                  <li
                    key={record.entryId}
                    className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-card/40 px-3 py-2"
                  >
                    <code className="truncate text-xs text-muted-foreground">{record.entryId}</code>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => void revoke(record.entryId)}
                    >
                      Revocar
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}

      {editing !== null && projectId !== null ? (
        <EntryDialog
          initial={editing}
          projectId={projectId}
          existingIds={entries.map((entry) => entry.id)}
          templates={templates}
          availableTags={availableTags}
          onClose={() => setEditing(null)}
          onSave={saveEntry}
        />
      ) : null}
    </div>
  );
}
